import React, {forwardRef, useLayoutEffect, useRef} from 'react';

import {cn} from '../lib/utils';

export type ButtonVariant =
  | 'default'
  | 'secondary'
  | 'outline'
  | 'ghost'
  | 'destructive'
  | 'accent'
  | 'link';

export type ButtonSize = 'default' | 'small' | 'large' | 'icon';

type ButtonOwnProps = {
  variant?: ButtonVariant;
  size?: ButtonSize;
  className?: string;
  children?: React.ReactNode;
};

type NativeAnchorProps = Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, keyof ButtonOwnProps>;
type NativeButtonProps = Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, keyof ButtonOwnProps>;

/**
 * `href` is the branch discriminator: supplying it renders a native anchor and
 * accepts anchor attributes, omitting it renders a native button and accepts
 * button attributes. There is no polymorphic `as` prop and no router-aware
 * link, so the rendered element keeps its own native semantics.
 */
export type ButtonProps =
  | (ButtonOwnProps & NativeButtonProps & {href?: never})
  | (ButtonOwnProps & NativeAnchorProps & {href: string});

// Colour, border, focus and disabled styling all resolve through the Layer 2
// `--compass-*` semantic roles. Geometry (radius, height, padding, type scale,
// ring width) uses Tailwind's own default scales, because Layer 2 exposes no
// geometry role and Layer 1 is never referenced directly by a component.
const base = [
  'relative inline-flex items-center justify-center gap-x-2 rounded-md border border-transparent',
  'text-sm font-semibold leading-tight no-underline',
  // The two overlay roles are translucent, so hover/active paint as a flat
  // background image above the variant's background colour and below the
  // label. One technique covers every variant, including the two with no
  // background of their own.
  'hover:bg-[image:linear-gradient(var(--compass-surface-hover),var(--compass-surface-hover))]',
  'active:bg-[image:linear-gradient(var(--compass-surface-active),var(--compass-surface-active))]',
  // Keyboard focus only, so a pointer press does not paint the ring.
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--compass-focus-ring)',
  // `:disabled` never matches an anchor, so this is the button branch only.
  'disabled:pointer-events-none disabled:border-transparent',
  'disabled:bg-(--compass-disabled-surface) disabled:text-(--compass-disabled-foreground)',
];

const variants: Record<ButtonVariant, string> = {
  default: 'bg-(--compass-primary) text-(--compass-on-primary)',
  secondary: 'bg-(--compass-surface-muted) text-(--compass-on-muted)',
  outline: 'bg-(--compass-surface) text-(--compass-on-surface) border-(--compass-border)',
  ghost: 'bg-transparent text-(--compass-on-surface)',
  destructive: 'bg-(--compass-destructive) text-(--compass-on-destructive)',
  // The role identity accent: tells one Compass role from another where several
  // share a page, such as the Launch cards. Painted from the accent pair the
  // enclosing surface or data-compass-role scope resolves.
  accent: 'bg-(--compass-accent) text-(--compass-on-accent)',
  link: 'bg-transparent text-(--compass-primary) underline underline-offset-4',
};

// Heights are floors, not caps, so a label that wraps grows the box and keeps
// its vertical padding instead of overflowing it. A consumer raising the floor
// should do so with `min-h-*`, not `h-*`. `h-fit` stands in for the fixed
// height these used to declare: a cross size other than `auto` is never
// stretched by a flex row, so a Button still sizes to its own label beside a
// taller sibling. A consumer opts into stretching with `h-auto`.
const sizes: Record<ButtonSize, string> = {
  default: 'h-fit min-h-9 px-4 py-2',
  small: 'h-fit min-h-8 px-3 py-1.5',
  large: 'h-fit min-h-10 px-6 py-2',
  icon: 'size-9 p-0',
};

/** Compass' house rule for a pointer target, in CSS pixels. */
const MIN_TARGET_SIZE = 44;

/**
 * What counts as a neighbouring control when a target is clipped. Deliberately
 * a closed enumeration of interactive elements: a rule that also matched
 * containers, padding edges or text nodes would clip targets that have nothing
 * beside them, which is a worse defect than the overhang it would be fixing.
 */
const INTERACTIVE_NEIGHBOURS = [
  'a[href]',
  'button',
  'input',
  'select',
  'textarea',
  'summary',
  '[role="button"]',
  '[role="link"]',
  '[role="checkbox"]',
  '[role="radio"]',
  '[role="switch"]',
  '[role="tab"]',
  '[role="menuitem"]',
  '[contenteditable="true"]',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

type Side = 'top' | 'right' | 'bottom' | 'left';

const SIDES: Side[] = ['top', 'right', 'bottom', 'left'];

/**
 * What was observed on a control's behalf, recorded when it registers. Reading
 * the parent back at cleanup time would be too late: React may already have
 * detached the control, which would leave a container observed for the lifetime
 * of the registry.
 */
type Registration = {
  layer: HTMLElement;
  parent: HTMLElement | null;
  root: HTMLElement;
  view: Window | null;
};

/** Every mounted control mapped to what was registered on its behalf. */
const registry = new Map<HTMLElement, Registration>();

let observer: ResizeObserver | null = null;
const observedViews = new Set<Window>();
const mutationObservers = new Map<HTMLElement, MutationObserver>();
let pending = false;

/**
 * Limit neighbour discovery to the closest ancestor that establishes the
 * control's positioning or scrolling context. A plain wrapper is the fallback,
 * which keeps an otherwise unbounded document scan local to the control.
 */
const getNeighbourScope = (control: HTMLElement, view: Window | null) => {
  const immediateParent = control.parentElement ?? control.ownerDocument.documentElement;

  if (!view) {
    return immediateParent;
  }

  let ancestor: HTMLElement | null = control.parentElement;

  while (ancestor && ancestor !== control.ownerDocument.documentElement) {
    const style = view.getComputedStyle(ancestor);
    const scrolls = [style.overflowX, style.overflowY].some((overflow) =>
      ['auto', 'scroll', 'hidden', 'clip'].includes(overflow)
    );

    if (style.position !== 'static' || scrolls) {
      return ancestor;
    }

    ancestor = ancestor.parentElement;
  }

  return immediateParent;
};

/** Include the nearest set of sibling wrappers around the current scope. */
const getCandidateScope = (control: HTMLElement, view: Window | null) => {
  const neighbourScope = getNeighbourScope(control, view);

  return neighbourScope.parentElement ?? neighbourScope;
};

const projectedBandsOverlap = (
  start: number,
  end: number,
  expansion: number,
  neighbourStart: number,
  neighbourEnd: number,
  neighbourExpansion: number
) =>
  start - expansion < neighbourEnd + neighbourExpansion &&
  neighbourStart - neighbourExpansion < end + expansion;

/**
 * One read phase, then one write phase. Batching the whole registry this way
 * means a mount of twenty-four Buttons settles in a single pass rather than
 * interleaving a measurement with a style write twenty-four times over.
 */
const settle = () => {
  pending = false;

  if (registry.size === 0) {
    return;
  }

  const candidateScopes = new Map<HTMLElement, Element[]>();
  const boxesByDocument = new Map<Document, Map<Element, DOMRect>>();
  const writes: {layer: HTMLElement; insets: Record<Side, number>}[] = [];

  // Rectangles are viewport-relative, so cache them only alongside the document
  // whose coordinate space they belong to.
  registry.forEach((_, control) => {
    const ownerDocument = control.ownerDocument;
    let boxes = boxesByDocument.get(ownerDocument);

    if (!boxes) {
      boxes = new Map<Element, DOMRect>();
      boxesByDocument.set(ownerDocument, boxes);
    }

    boxes.set(control, control.getBoundingClientRect());
  });

  registry.forEach(({layer}, control) => {
    const ownerDocument = control.ownerDocument;
    const view = ownerDocument.defaultView;

    if (!view) {
      return;
    }

    // Recompute this after every observed layout change: a consumer can move a
    // mounted control beneath a different positioned or scrolling ancestor.
    const candidateScope = getCandidateScope(control, view);
    let scopedCandidates = candidateScopes.get(candidateScope);

    if (!scopedCandidates) {
      scopedCandidates = Array.from(candidateScope.querySelectorAll(INTERACTIVE_NEIGHBOURS));
      candidateScopes.set(candidateScope, scopedCandidates);
    }

    const boxes = boxesByDocument.get(ownerDocument);

    if (!boxes) {
      return;
    }

    const box = boxes.get(control);

    if (!box) {
      return;
    }

    // Read the border rather than assuming the 1px `base` declares: `cn` lets a
    // consumer `border-2` win its utility group, and an assumed width would
    // silently shift every inset on that control.
    const style = view.getComputedStyle(control);
    const border: Record<Side, number> = {
      top: parseFloat(style.borderTopWidth) || 0,
      right: parseFloat(style.borderRightWidth) || 0,
      bottom: parseFloat(style.borderBottomWidth) || 0,
      left: parseFloat(style.borderLeftWidth) || 0,
    };

    const wantedX = Math.max(0, (MIN_TARGET_SIZE - box.width) / 2);
    const wantedY = Math.max(0, (MIN_TARGET_SIZE - box.height) / 2);
    const expansion: Record<Side, number> = {
      top: wantedY,
      right: wantedX,
      bottom: wantedY,
      left: wantedX,
    };

    // The widened query includes native controls in sibling wrappers. Registered
    // Buttons are retained across more distant scopes in this document, and the
    // cheap range check below removes controls that are nowhere near this one.
    const registeredCandidates = Array.from(registry.keys()).filter(
      (element) => element.ownerDocument === ownerDocument
    );
    const candidates = new Set<Element>([...scopedCandidates, ...registeredCandidates]);

    candidates.forEach((element) => {
      if (
        element.ownerDocument !== ownerDocument ||
        element === control ||
        control.contains(element) ||
        element.contains(control) ||
        element.matches(':disabled') ||
        element.closest('[inert]')
      ) {
        return;
      }

      let rect = boxes.get(element);

      if (!rect) {
        rect = element.getBoundingClientRect();
        boxes.set(element, rect);
      }

      // A `display: none` element reports DOMRect{0,0,0,0}, which without this
      // guard reads as a control parked at the viewport origin.
      if (rect.width <= 0 || rect.height <= 0) {
        return;
      }

      if (
        rect.right < box.left - MIN_TARGET_SIZE ||
        rect.left > box.right + MIN_TARGET_SIZE ||
        rect.bottom < box.top - MIN_TARGET_SIZE ||
        rect.top > box.bottom + MIN_TARGET_SIZE
      ) {
        return;
      }

      const registeredNeighbour = registry.has(element as HTMLElement);
      const neighbourX = registeredNeighbour
        ? Math.max(0, (MIN_TARGET_SIZE - rect.width) / 2)
        : 0;
      const neighbourY = registeredNeighbour
        ? Math.max(0, (MIN_TARGET_SIZE - rect.height) / 2)
        : 0;
      const horizontalTargetsOverlap = projectedBandsOverlap(
        box.left,
        box.right,
        wantedX,
        rect.left,
        rect.right,
        neighbourX
      );
      const verticalTargetsOverlap = projectedBandsOverlap(
        box.top,
        box.bottom,
        wantedY,
        rect.top,
        rect.bottom,
        neighbourY
      );

      // Clip only when the projected targets overlap on the perpendicular
      // axis. Offset controls keep their full target, while facing targets meet
      // at the measured gap's midpoint regardless of paint or stacking order.
      if (horizontalTargetsOverlap && rect.bottom <= box.top) {
        expansion.top = Math.min(expansion.top, (box.top - rect.bottom) / 2);
      }
      if (horizontalTargetsOverlap && rect.top >= box.bottom) {
        expansion.bottom = Math.min(expansion.bottom, (rect.top - box.bottom) / 2);
      }
      if (verticalTargetsOverlap && rect.right <= box.left) {
        expansion.left = Math.min(expansion.left, (box.left - rect.right) / 2);
      }
      if (verticalTargetsOverlap && rect.left >= box.right) {
        expansion.right = Math.min(expansion.right, (rect.left - box.right) / 2);
      }
    });

    // The layer's containing block is the control's padding box, so backing
    // every inset out by that side's border width makes a zero expansion land
    // exactly on the border box — never inside it.
    writes.push({
      layer,
      insets: {
        top: -(border.top + expansion.top),
        right: -(border.right + expansion.right),
        bottom: -(border.bottom + expansion.bottom),
        left: -(border.left + expansion.left),
      },
    });
  });

  writes.forEach(({layer, insets}) => {
    SIDES.forEach((side) => {
      const value = `${insets[side]}px`;

      // Skipping an unchanged write keeps a repeated pass a genuine no-op, so a
      // pass cannot feed its own observations back into another one.
      if (layer.style.getPropertyValue(side) !== value) {
        layer.style.setProperty(side, value);
      }
    });
  });
};

/**
 * Microtasks drain before paint and before a browser test's next action, so
 * settling completes before anything — a user or a spec — can observe the
 * unsettled geometry.
 */
const schedule = () => {
  if (pending) {
    return;
  }

  pending = true;
  queueMicrotask(settle);
};

const handleMutations: MutationCallback = (records) => {
  const layers = new Set<Node>(Array.from(registry.values(), ({layer}) => layer));
  const relevantScopes = Array.from(registry.keys()).flatMap((control) => {
    const view = control.ownerDocument.defaultView;

    return view ? [{control, scope: getCandidateScope(control, view)}] : [];
  });
  const hasLayoutMutation = records.some((record) => {
    if (
      record.type === 'attributes' &&
      record.attributeName === 'style' &&
      layers.has(record.target)
    ) {
      return false;
    }

    if (record.type !== 'attributes' && record.type !== 'childList') {
      return false;
    }

    return relevantScopes.some(
      ({control, scope}) =>
        record.target.ownerDocument === control.ownerDocument &&
        (scope.contains(record.target) || record.target.contains(scope))
    );
  });

  if (hasLayoutMutation) {
    schedule();
  }
};

const startObserving = (control: HTMLElement, registration: Registration) => {
  if (!observer) {
    observer = new ResizeObserver(schedule);
  }

  // One resize listener per window the registry actually reaches, rather than
  // one for whichever window the first control happened to arrive from.
  if (registration.view && !observedViews.has(registration.view)) {
    observedViews.add(registration.view);
    registration.view.addEventListener('resize', schedule);
    // Capture scrolls from every container in this document. Scroll events do
    // not bubble, and a neighbour in another scroller can move relative to the
    // control without either element resizing.
    registration.view.addEventListener('scroll', schedule, true);
  }

  if (!mutationObservers.has(registration.root)) {
    const mutationObserver = new MutationObserver(handleMutations);
    // The document root is the smallest stable surface that also covers
    // ancestors, portals and neighbouring controls in separate containers.
    mutationObserver.observe(registration.root, {
      attributes: true,
      attributeFilter: [
        'class',
        'style',
        'hidden',
        'open',
        'href',
        'role',
        'tabindex',
        'contenteditable',
        // `disabled` and `inert` decide whether a neighbour participates in
        // clipping at all (see the candidate filter above). Without them here
        // the observer never wakes on a neighbour being enabled or disabled,
        // so the clip computed against its old state survives: a control that
        // has just become interactive keeps a target overlapping it, which is
        // the press-stealing this component exists to prevent.
        'disabled',
        'inert',
      ],
      childList: true,
      subtree: true,
    });
    mutationObservers.set(registration.root, mutationObserver);
  }

  // The layer itself is never resize-observed. It is absolutely positioned, so
  // writing its insets cannot dirty anything measured here; the mutation
  // callback separately ignores those writes to close its feedback loop.
  observer.observe(control);

  if (registration.parent) {
    observer.observe(registration.parent);
  }

  // A sibling above growing moves this control without resizing either it or
  // its parent, so the document is observed as well.
  observer.observe(registration.root);
};

const stopObserving = (control: HTMLElement, registration: Registration) => {
  observer?.unobserve(control);

  if (registry.size === 0) {
    observer?.disconnect();
    observer = null;
    mutationObservers.forEach((mutationObserver) => mutationObserver.disconnect());
    mutationObservers.clear();
    observedViews.forEach((view) => {
      view.removeEventListener('resize', schedule);
      view.removeEventListener('scroll', schedule, true);
    });
    observedViews.clear();

    return;
  }

  // The parent, the document and the window are shared with every sibling
  // control, so each is released only once the last control sharing it has gone.
  const remaining = Array.from(registry.values());

  if (registration.parent && !remaining.some(({parent}) => parent === registration.parent)) {
    observer?.unobserve(registration.parent);
  }

  if (!remaining.some(({root}) => root === registration.root)) {
    observer?.unobserve(registration.root);
    mutationObservers.get(registration.root)?.disconnect();
    mutationObservers.delete(registration.root);
  }

  if (registration.view && !remaining.some(({view}) => view === registration.view)) {
    registration.view.removeEventListener('resize', schedule);
    registration.view.removeEventListener('scroll', schedule, true);
    observedViews.delete(registration.view);
  }
};

/**
 * Expands the pointer target toward at least 44x44 CSS pixels without forcing
 * the visible border box to that size. The span is a child of the control, so a
 * pointer event anywhere over it still resolves to the control.
 *
 * The geometry is measured rather than declared, because a fixed square cannot
 * know what is beside it: each side is inset out to the smaller of the
 * expansion this control wants and half the gap to the nearest interactive
 * neighbour on that side. Two neighbours therefore meet on the midpoint of the
 * gap between them and never overlap, so neither can steal the other's press.
 *
 * Before measurement the layer is exactly the control's padding box. That
 * fail-safe direction is deliberate: a target that under-reaches is an
 * accessibility shortfall, whereas one that overhangs takes a press away from a
 * different control.
 *
 * The inline insets require the control to remain the layer's containing block.
 * `base` supplies `relative`; a consumer `className` may override positioning,
 * so it must preserve that containing-block relationship. `absolute` and
 * `fixed` remain positioned ancestors but replace the control's layout contract;
 * `static` removes the containing block outright and lets the layer overhang.
 *
 * Deliberately carries no `@media (pointer: fine)` escape hatch: Compass
 * applies the 44px house rule on every pointer type, and hiding the layer on
 * fine pointers would leave desktop with the visible box as its only target.
 */
function PointerTarget() {
  const layer = useRef<HTMLSpanElement>(null);

  // A layout effect, not an effect: it runs before paint, so the settled
  // geometry is the first geometry anything can measure.
  useLayoutEffect(() => {
    const span = layer.current;
    const control = span?.parentElement;

    if (!span || !control) {
      return undefined;
    }

    const view = control.ownerDocument.defaultView;
    const registration: Registration = {
      layer: span,
      parent: control.parentElement,
      root: control.ownerDocument.documentElement,
      view,
    };

    registry.set(control, registration);
    startObserving(control, registration);
    schedule();

    return () => {
      registry.delete(control);
      stopObserving(control, registration);
    };
  }, []);

  return (
    <span
      ref={layer}
      aria-hidden="true"
      data-slot="pointer-target"
      // `inset-0` is the pre-measurement padding box, and it is a class rather
      // than an inline style so that `settle` is the only writer of the inline
      // insets it overrides. A re-render cannot then compete for those four
      // properties with the geometry already measured onto them.
      className="absolute inset-0"
    />
  );
}

/**
 * A button or a link, chosen by `href`, with a pointer target larger than its
 * visible box.
 *
 * The 44x44 target is best effort. Where controls are tightly spaced, facing
 * targets are clipped at the geometric midpoint of the gap between them, so
 * they never overlap and a press always activates the control it is over —
 * non-overlap takes precedence over reaching 44x44. Every built-in size keeps a
 * target of at least 24x24 CSS pixels, the WCAG 2.2 AA minimum, because a
 * target is never clipped inside the control's own border box.
 *
 * The primitive never changes layout to make room: it adds no margin, padding
 * or spacing, and it does not move a consumer's controls. A consumer override
 * or layout that leaves less than 24x24 available has to supply more size or
 * more spacing itself.
 */
export const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(function Button(
  {variant = 'default', size = 'default', className, children, ...props},
  ref
) {
  const classes = cn(base, variants[variant], sizes[size], className);

  if (typeof props.href === 'string') {
    const anchorProps = props as NativeAnchorProps;

    return (
      <a {...anchorProps} className={classes} ref={ref as React.ForwardedRef<HTMLAnchorElement>}>
        <PointerTarget />
        {children}
      </a>
    );
  }

  const {type = 'button', ...buttonProps} = props as NativeButtonProps;

  return (
    <button
      {...buttonProps}
      type={type}
      className={classes}
      ref={ref as React.ForwardedRef<HTMLButtonElement>}>
      <PointerTarget />
      {children}
    </button>
  );
});
